import React from 'react';
import { DraftListItem as DraftListItemType } from '../../services/draftService';
import { DraftListItem } from './DraftListItem';

interface DraftsListProps {
  drafts: DraftListItemType[];
  loading: boolean;
  error: string | null;
  onSelect: (draftId: string) => void;
  onDelete: (draftId: string) => void;
  onRetry: () => void;
}

/**
 * Loading skeleton for draft list items
 */
const DraftListSkeleton: React.FC = () => (
  <div className="flex gap-4 p-4 border border-gray-200 rounded-lg animate-pulse">
    <div className="flex-shrink-0 w-20 h-20 rounded-md bg-gray-200" />
    <div className="flex-1 space-y-2">
      <div className="h-4 bg-gray-200 rounded w-3/4" />
      <div className="h-3 bg-gray-200 rounded w-1/2" />
      <div className="h-3 bg-gray-200 rounded w-2/3" />
      <div className="h-3 bg-gray-200 rounded w-full" />
    </div>
  </div>
);

/**
 * Empty state when no drafts exist
 */
const EmptyState: React.FC = () => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <div className="w-16 h-16 mb-4 text-gray-300">
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    </div>
    <h3 className="text-lg font-medium text-gray-900 mb-1">No drafts yet</h3>
    <p className="text-sm text-gray-500 max-w-xs">
      Start creating an event and your progress will be automatically saved here.
    </p>
  </div>
);

/**
 * Error state with retry option
 */
const ErrorState: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <div className="w-16 h-16 mb-4 text-red-400">
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
    </div>
    <h3 className="text-lg font-medium text-gray-900 mb-1">Failed to load drafts</h3>
    <p className="text-sm text-gray-500 mb-4">{message}</p>
    <button
      onClick={onRetry}
      className="px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors"
    >
      Try again
    </button>
  </div>
);

/**
 * Scrollable list of draft items
 */
export const DraftsList: React.FC<DraftsListProps> = ({
  drafts,
  loading,
  error,
  onSelect,
  onDelete,
  onRetry,
}) => {
  if (loading) {
    return (
      <div className="space-y-3">
        <DraftListSkeleton />
        <DraftListSkeleton />
        <DraftListSkeleton />
      </div>
    );
  }

  if (error) {
    return <ErrorState message={error} onRetry={onRetry} />;
  }

  if (drafts.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
      {drafts.map((draft) => (
        <DraftListItem
          key={draft.id}
          draft={draft}
          onSelect={onSelect}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
};
